/**
 * Connection-staleness classifier for the Apex bridge.
 *
 * The agent polls the Apex every ~5s and stamps `fetchedAtMs` on the
 * snapshot it returns to /state. The dashboard polls /state every 2s.
 * "Staleness" here is purely a UI signal: we want a tight green/yellow/red
 * status that flips off green within a few polls so dead data never masks
 * a real problem.
 *
 *   connected (ok)     : age <  6 s   ( ≤ 3 dashboard polls )
 *   stale     (warn)   : 6 s ≤ age < 30 s
 *   offline   (danger) : 30 s ≤ age, OR fetchedAtMs is undefined / negative
 *
 * Window edges are inclusive on the low side, exclusive on the high side
 * so a value sitting exactly on the boundary deterministically rounds up
 * to the more-permissive bucket.
 */

export type ApexConnectionStatus = 'connected' | 'stale' | 'offline';

export const APEX_STALE_AFTER_MS = 6_000;
export const APEX_OFFLINE_AFTER_MS = 30_000;

/**
 * Classify the freshness of an Apex snapshot.
 *
 * @param fetchedAtMs Wall-clock ms when the snapshot was produced, or
 *   `undefined`/`null` when no snapshot has ever been received.
 * @param nowMs Optional override for "now" — used in tests. Defaults to
 *   `Date.now()`.
 */
export function apexConnectionStatus(
  fetchedAtMs: number | null | undefined,
  nowMs: number = Date.now(),
): ApexConnectionStatus {
  if (typeof fetchedAtMs !== 'number' || !Number.isFinite(fetchedAtMs) || fetchedAtMs < 0) {
    return 'offline';
  }
  const age = nowMs - fetchedAtMs;
  if (age >= APEX_OFFLINE_AFTER_MS) return 'offline';
  if (age >= APEX_STALE_AFTER_MS) return 'stale';
  return 'connected';
}

/** Pretty seconds-ago label, e.g. `"3s"` or `"42s"`. Clamped to 0 minimum. */
export function formatAgeSeconds(fetchedAtMs: number | null | undefined, nowMs: number = Date.now()): string {
  if (typeof fetchedAtMs !== 'number' || !Number.isFinite(fetchedAtMs)) return '—';
  const seconds = Math.max(0, Math.floor((nowMs - fetchedAtMs) / 1000));
  return `${seconds}s`;
}